import { Box, Popper, IconButton, TableCell } from "@material-ui/core";
import { Check, Close, GetApp, Link } from "@material-ui/icons";
import { Alert as ToastAlert } from "@material-ui/lab";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { RequestedVideo, useLazyGetShareLinkQuery } from "@/api/requestedVideos";
import { ImageNotFound } from "@/common/components/SVG/Svg";
import { Severity } from "@/store/alerts/slice";
import { OnFeatureFlag } from "@/utilities/OnFeatureFlag";

import useStyles, { Cell } from "./RequestedVideoRow.styles";


export interface RequestedVideoRowProps {
    liveUnitId: number;
    row: RequestedVideo;
}

const RequestedVideoRow: FC<RequestedVideoRowProps> = ({ liveUnitId, row }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const { t, i18n } = useTranslation("translation", { keyPrefix: "components.requestedVideos" });
    const [getShareLink] = useLazyGetShareLinkQuery();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const styles = useStyles();

    const handleShare = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const buttonElement = e.currentTarget;

        try {
            const shareLink = await getShareLink({
                dewarped: !!row.dewarpedVideoUrl,
                liveUnitId,
                videoId: row.id,
            }).unwrap();

            navigator.clipboard.writeText(shareLink);

            setAnchorEl(buttonElement);
        } catch (error) {
            onShareLinkFail();
        }
    };

    const handleClose = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.stopPropagation();
        setAnchorEl(null);
    };

    const onShareLinkFail = () => {
        closeSnackbar(row.id);

        const alert = (
            <ToastAlert severity="error">
                {t("errors.shareLink", { videoId: row.id })}
            </ToastAlert>
        );

        enqueueSnackbar(alert, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8000,
            preventDuplicate: true,
            variant: Severity.Error,
        });
    };

    // This function is being commented out until the bug mentioned in the comment below ~line 203 is resolved
    // const onDownloadFail = () => {
    //     closeSnackbar(row.id);

    //     const alert = (
    //         <ToastAlert severity="error">
    //             {t("errors.download")}
    //         </ToastAlert>
    //     );

    //     enqueueSnackbar(alert, {
    //         anchorOrigin: {
    //             vertical: "bottom",
    //             horizontal: "right",
    //         },
    //         autoHideDuration: 8000,
    //         preventDuplicate: true,
    //         variant: Severity.Error,
    //     });
    // };

    useEffect(() => {
        if (anchorEl) {
            const timer = setTimeout(() => setAnchorEl(null), 5000);

            return () => clearTimeout(timer);
        }
    }, [anchorEl]);

    return (
        <>
            <TableCell style={{ width: 160 }}>
                {(row.dewarpedVideoUrl || row.videoUrl) ?
                    <video className={styles.thumbnail} src={`${row.dewarpedVideoUrl ?? row.videoUrl}#t=0.1`} />
                    :
                    <div className={styles.thumbnail}>
                        <div className={styles.noImageBackground}>
                            <ImageNotFound />
                        </div>
                    </div>
                }
            </TableCell>
            <Cell>
                {row.name ? <div>{row.name}</div> : <div>&#8212;</div>}
                <div>{row.isThermal && t("thermal")}</div>
                <div>{row.dewarpedVideoUrl && t("panoDewarped")}</div>
            </Cell>
            <Cell>
                {row.recordedDatetime && row.locationTimezone ? (
                    <>
                        <div>
                            {DateTime
                                .fromISO(row.recordedDatetime, { zone: "utc" })
                                .setZone(row.locationTimezone)
                                .setLocale(i18n.language)
                                .toLocaleString(DateTime.DATE_MED)
                                .toUpperCase()
                            }
                        </div>
                        <div>
                            {DateTime
                                .fromISO(row.recordedDatetime, { zone: "utc" })
                                .setZone(row.locationTimezone)
                                .setLocale(i18n.language)
                                .toLocaleString({
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    timeZoneName: "short",
                                })
                            }
                        </div>
                    </>
                ) : (
                    <div>&#8212;</div>
                )}
            </Cell>
            <Cell>
                {row.requestedDatetime && row.locationTimezone ? (
                    <>
                        <div>
                            {DateTime
                                .fromISO(row.requestedDatetime, { zone: "utc" })
                                .setZone(row.locationTimezone)
                                .setLocale(i18n.language)
                                .toLocaleString(DateTime.DATE_MED)
                                .toUpperCase()
                            }
                        </div>
                        <div>
                            {DateTime
                                .fromISO(row.requestedDatetime, { zone: "utc" })
                                .setZone(row.locationTimezone)
                                .setLocale(i18n.language)
                                .toLocaleString({
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    timeZoneName: "short",
                                })
                            }
                        </div>
                    </>
                ) : (
                    <div>&#8212;</div>
                )}
            </Cell>
            <Cell>
                {row.requestedByName || row.requestedByEmail ? (
                    <>
                        <div>{row.requestedByName}</div>
                        <div>{row.requestedByEmail}</div>
                    </>
                ) : (
                    <div>&#8212;</div>
                )}
            </Cell>
            <TableCell style={{ width: 160 }}>
                <Box display="flex" justifyContent="space-around">
                    <OnFeatureFlag flag="requested_video_share">
                        <IconButton id={`requested-videos_requested-video-row_${row.id}_share-button`} onClick={handleShare}>
                            <Link />
                        </IconButton>
                    </OnFeatureFlag>
                    <a
                        id={`requested-videos_requested-video-row_${row.id}_download-button`}
                        download
                        data-testid="download-button"
                        href={row.dewarpedVideoUrl ?? row.videoUrl}
                        onClick={e => e.stopPropagation()}
                         // This doesnt work/ on error is not a valid attribute for anchor tags - Does not fire in case of error
                        // If we want to handle error case we need to handle throgh javascript
                        // onError={onDownloadFail}
                        // onError={onDownloadFail}
                    >
                        <IconButton>
                            <GetApp />
                        </IconButton>
                    </a>
                </Box>

                <Popper
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    placement="left"
                >
                    <div className={styles.linkPopper}>
                        <Check color="secondary" />
                        <Box margin="0px 24px 0px 8px">{t("linkPopper")}</Box>
                        <Close onClick={handleClose} className={styles.close} />
                    </div>
                </Popper>
            </TableCell>
        </>
    );
};

export default RequestedVideoRow;
